import { type FC, type ReactNode, createContext, useContext } from "react";

/**
 * Override values for document/page routing.
 * When provided via DocumentIdProvider, these values will be used
 * instead of extracting them from the URL router.
 */
export interface DocumentIdOverride {
	workspaceName?: string;
	documentId: string;
	pageSlug?: string;
	/** Direct page ID override - when provided, bypasses slug-to-id lookup */
	pageId?: string;
}

const DocumentIdContext = createContext<DocumentIdOverride | null>(null);

/**
 * Hook to get the current document ID override from context.
 * Returns null if no override is provided (i.e., use router-based values).
 */
export const useDocumentIdOverride = (): DocumentIdOverride | null => useContext(DocumentIdContext);

/**
 * Provider component that overrides the document/page IDs returned by useRouteParams.
 * Use this to render components that depend on route params (like BigRichTextEditor)
 * in contexts where the URL doesn't reflect the document being displayed (e.g., template previews).
 */
export const DocumentIdProvider: FC<{
	value: DocumentIdOverride;
	children: ReactNode;
}> = ({ value, children }) => (
	<DocumentIdContext.Provider value={value}>{children}</DocumentIdContext.Provider>
);
